import { supabase, hasSupabase } from './supabase.js';

// Loads the buyer or caterer row + profile into the user shape the rest of
// caterbase.jsx expects: { id, role, full_name, organisation, email }
async function hydrateUser(authUser) {
  if (!supabase || !authUser) return null;

  const { data: profile } = await supabase
    .from('profiles')
    .select('role, full_name')
    .eq('id', authUser.id)
    .maybeSingle();

  if (!profile) {
    // Profile not yet created (mid-registration); return minimal shape
    return { id: authUser.id, email: authUser.email, role: null, full_name: null, organisation: null };
  }

  let organisation = null;
  let subscription_status = null;
  let subscription_plan = null;
  let verification_status = null;
  if (profile.role === 'buyer') {
    const { data } = await supabase.from('buyers').select('organisation_name, subscription_status, subscription_plan').eq('profile_id', authUser.id).maybeSingle();
    organisation = data?.organisation_name || null;
    subscription_status = data?.subscription_status || null;
    subscription_plan = data?.subscription_plan || null;
  } else if (profile.role === 'caterer') {
    const { data } = await supabase.from('caterers').select('company_name, verification_status').eq('profile_id', authUser.id).maybeSingle();
    organisation = data?.company_name || null;
    verification_status = data?.verification_status || null;
  } else if (profile.role === 'admin') {
    organisation = 'Caterbase';
  }

  return { id: authUser.id, email: authUser.email, role: profile.role, full_name: profile.full_name, organisation, subscription_status, subscription_plan, verification_status };
}

export async function getCurrentUser() {
  if (!hasSupabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return hydrateUser(user);
}

export function onAuthChange(callback) {
  if (!hasSupabase) return { unsubscribe: () => {} };
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
    const user = await hydrateUser(session?.user || null);
    callback(user);
  });
  return subscription;
}

export async function signIn({ email, password }) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return hydrateUser(data.user);
}

export async function signUp({ email, password, role, full_name, organisation, org_type, vat_number }) {
  if (!hasSupabase) throw new Error('Supabase not configured');
  if (!['buyer','caterer'].includes(role)) throw new Error('Invalid role');

  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  if (!data.user) throw new Error('Sign-up succeeded but no user returned (check email confirmation settings)');

  const userId = data.user.id;

  const { error: profileErr } = await supabase.from('profiles').insert({
    id: userId, role, full_name,
  });
  if (profileErr) throw profileErr;

  if (role === 'buyer') {
    const { error: buyerErr } = await supabase.from('buyers').insert({
      profile_id: userId,
      organisation_name: organisation,
      organisation_type: org_type || null,
    });
    if (buyerErr) throw buyerErr;
  } else if (role === 'caterer') {
    const { error: catErr } = await supabase.from('caterers').insert({
      profile_id: userId,
      company_name: organisation,
      vat_number: vat_number || null,
    });
    if (catErr) throw catErr;
  }

  return hydrateUser(data.user);
}

export async function signOut() {
  if (!hasSupabase) return;
  await supabase.auth.signOut();
}
